/**
 * Utility functions for order management
 */

/**
 * Generates a 6-digit order number
 * @returns A random 6-digit number between 100000 and 999999
 */
export const generateOrderNumber = (): number => {
  const min = 100000;
  const max = 999999;

  // crypto.randomInt gives better spread than Math.random alone
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return min + (buf[0] % (max - min + 1));
  }

  return min + Math.floor(Math.random() * (max - min + 1));
};

/**
 * Formats an order number for display
 * @param orderNumber - The order number to format
 * @returns Formatted order number string
 */
export const formatOrderNumber = (orderNumber: number | string): string => {
  return orderNumber.toString().padStart(6, '0');
};

/**
 * Validates if a string is a valid 6-digit order number
 * @param orderNumber - The order number to validate
 * @returns True if valid, false otherwise
 */
export const isValidOrderNumber = (orderNumber: string): boolean => {
  const num = parseInt(orderNumber, 10);
  return !isNaN(num) && num >= 100000 && num <= 999999;
};

/**
 * Generates a unique order number that doesn't conflict with existing ones
 * @param existingOrderNumbers - Array of existing order numbers to avoid conflicts
 * @returns A unique 6-digit order number
 */
export const generateUniqueOrderNumber = (existingOrderNumbers: number[] = []): number => {
  let orderNumber: number;
  let attempts = 0;
  const maxAttempts = 100; // Prevent infinite loops
  
  do {
    orderNumber = generateOrderNumber();
    attempts++;
    
    // If we've tried too many times, just return the current number
    // (in practice, with 900,000 possible numbers, this should rarely happen)
    if (attempts >= maxAttempts) {
      console.warn('Maximum attempts reached for generating unique order number');
      break;
    }
  } while (existingOrderNumbers.includes(orderNumber));
  
  return orderNumber;
};

/**
 * Converts an old ORD- format order number to a new 6-digit format
 * @param oldOrderNumber - The old order number in ORD- format
 * @returns A new 6-digit order number
 */
export const convertOldOrderNumber = (oldOrderNumber: string): number => {
  // If it's already a 6-digit number, return it
  if (isValidOrderNumber(oldOrderNumber)) {
    return parseInt(oldOrderNumber, 10);
  }
  
  // If it's in the old ORD- format, generate a new one
  if (oldOrderNumber.startsWith('ORD-')) {
    return generateOrderNumber();
  }
  
  // For any other format, generate a new one
  return generateOrderNumber();
}; 